import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Typography,
  Container,
  Button,
  Chip,
  Divider,
  Paper,
  Grid,
  Avatar,
  Skeleton,
  Card,
  CardContent,

  CardActionArea,
  useTheme,
  IconButton,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  Snackbar,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DateRangeIcon from '@mui/icons-material/DateRange';

import ShareIcon from '@mui/icons-material/Share';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import { alpha } from '@mui/material/styles';
import { fetchPost, fetchPosts } from '../services/api';
import { useAuth } from '../context/AuthContext';

function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAdmin } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isBookmarked, setIsBookmarked] = useState(false);

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleSocialShare = (platform) => {
    const url = window.location.href;
    const text = post?.title || 'Check out this post!';
    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handleWebShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: 'Check out this post!',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      setSnackbarMessage('Link copied to clipboard!');
      setSnackbarOpen(true);
    }
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    setSnackbarMessage(isBookmarked ? 'Removed from bookmarks' : 'Post saved to bookmarks');
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        const data = await fetchPost(id);
        setPost(data);

        // Load related posts based on category
        if (data.category) {
          loadRelatedPosts(data.category, data._id || data.id);
        }

        // Scroll to top when post loads
        window.scrollTo(0, 0);
      } catch (err) {
        console.error('Error loading post:', err);
        setError('Failed to load post. It might have been deleted or is temporarily unavailable.');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id]);

  // Load related posts based on category
  const loadRelatedPosts = async (category, currentPostId) => {
    try {
      const allPosts = await fetchPosts();
      // Filter posts by the same category, excluding the current post
      const filtered = allPosts.filter(p => p.category === category && (p._id || p.id) !== currentPostId);
      // Limit to 3 related posts
      setRelatedPosts(filtered.slice(0, 3));
    } catch (error) {
      console.error('Error loading related posts:', error);
    }
  };

  // Handle search submission
  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${searchQuery}`);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };



  // Get post thumbnail
  const getPostThumbnail = (post) => {
    if (post?.thumbnailData?.data) {
      return post.thumbnailData.data;
    }
    return 'https://via.placeholder.com/1200x600?text=No+Image';
  };

  // Convert Markdown to HTML (enhanced implementation)
  const renderContent = (content) => {
    let html = content
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Headings (Shifted to avoid multiple H1s)
      .replace(/^# (.*$)/gm, '<h2>$1</h2>')
      .replace(/^## (.*$)/gm, '<h3>$1</h3>')
      .replace(/^### (.*$)/gm, '<h4>$1</h4>')
      .replace(/^#### (.*$)/gm, '<h5>$1</h5>')
      // Blockquotes
      .replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>')
      // Unordered lists - replace the beginning of each line
      .replace(/^- (.*$)/gm, '<li>$1</li>')
      // Ordered lists - replace the beginning of each line
      .replace(/^\d+\. (.*$)/gm, '<li>$1</li>')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br />');

    // Wrap the content in paragraphs
    html = `<p>${html}</p>`;

    // Clean up any empty paragraphs
    html = html.replace(/<p><\/p>/g, '');

    // Add wrapper divs for lists
    html = html.replace(/(<li>.*?<\/li>)\s*(<li>.*?<\/li>)/gs, '<ul>$1$2</ul>');

    // Replace image placeholders if any
    if (post.imageData) {
      post.imageData.forEach(img => {
        const placeholder = new RegExp(`!\\[${img.name}\\]\\(${img.name}\\)`, 'g');
        html = html.replace(placeholder, `<div class="post-image"><img src="${img.data}" alt="${img.name}" style="max-width: 100%; margin: 20px 0; border-radius: 8px;" /></div>`);
      });
    }

    return { __html: html };
  };

  // Loading UI with enhanced skeleton
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 2 }}>
          <Skeleton variant="text" width={200} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={100} />
        </Box>

        <Skeleton variant="rectangular" height={50} width="60%" sx={{ mb: 2, borderRadius: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
          <Skeleton variant="rounded" width={100} height={32} />
          <Skeleton variant="rounded" width={100} height={32} />
        </Box>

        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mb: 4 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="text" height={30} />
            <Skeleton variant="text" height={30} />
            <Skeleton variant="text" height={30} width="90%" />

            <Box sx={{ my: 3 }}>
              <Skeleton variant="text" height={24} />
              <Skeleton variant="text" height={24} />
              <Skeleton variant="text" height={24} width="95%" />
              <Skeleton variant="text" height={24} width="80%" />
            </Box>

            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, my: 3 }} />

            <Box sx={{ my: 3 }}>
              <Skeleton variant="text" height={24} />
              <Skeleton variant="text" height={24} />
              <Skeleton variant="text" height={24} width="90%" />
              <Skeleton variant="text" height={24} width="85%" />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2, mb: 3 }} />
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  // Error UI with enhanced styling
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h5" color="error" gutterBottom sx={{ fontWeight: 'bold' }}>
            Error Loading Post
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            color="secondary"
            sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
          >
            Go Back to Homepage
          </Button>
        </Paper>
      </Container>
    );
  }

  // No post found UI
  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            Post Not Found
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            The post you're looking for doesn't exist or has been removed.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            color="secondary"
            sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
          >
            Go Back to Homepage
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 6 }}>
      <Helmet>
        <title>{`${post.title} | WorldWise Scholar`}</title>
        <meta name="description" content={post.content ? post.content.substring(0, 160) : "Read more about this scholarship opportunity on WorldWise Scholar."} />
        <link rel="canonical" href={`https://worldwisescholar.online/blog/${id}`} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={`${post.title} | WorldWise Scholar`} />
        <meta property="og:description" content={post.content ? post.content.substring(0, 160) : ""} />
        <meta property="og:url" content={`https://worldwisescholar.online/blog/${id}`} />
        {post.thumbnailData?.data && <meta property="og:image" content={post.thumbnailData.data} />}

        {/* Article Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "image": post.thumbnailData?.data ? [post.thumbnailData.data] : ["https://worldwisescholar.online/hero_education.png"],
            "datePublished": post.created_at || new Date().toISOString(),
            "dateModified": post.updated_at || post.created_at || new Date().toISOString(),
            "author": {
              "@type": "Person",
              "name": "WorldWise Scholar Team"
            },
            "publisher": {
              "@type": "Organization",
              "name": "WorldWise Scholar",
              "logo": {
                "@type": "ImageObject",
                "url": "https://worldwisescholar.online/hero_education.png"
              }
            },
            "description": post.content ? post.content.substring(0, 160) : "",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://worldwisescholar.online/blog/${id}`
            }
          })}
        </script>
      </Helmet>
      {/* Hero Section with Featured Image */}
      {post.thumbnailData && (
        <Box
          sx={{
            position: 'relative',
            height: { xs: 250, md: 400 },
            width: '100%',
            bgcolor: '#000',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${getPostThumbnail(post)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.7)',
              zIndex: 1
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '50%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))',
              zIndex: 2
            }
          }}
        >
          {/* Overlay content */}
          <Container
            maxWidth="lg"
            sx={{
              position: 'relative',
              zIndex: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              pb: 3
            }}
          >
            {/* Remove the category chip that was here */}
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 1,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              {post.title}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, color: 'white' }}>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DateRangeIcon sx={{ color: 'rgba(255,255,255,0.8)', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {formatDate(post.created_at)}
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Update the Container spacing and Breadcrumbs Paper component */}
      <Container
        maxWidth="lg"
        sx={{
          mt: post.thumbnailData ? -3 : 4,
          position: 'relative',
          zIndex: 5,
          px: { xs: 3, sm: 4 }  // Add horizontal padding
        }}
      >
        {/* Breadcrumbs with improved spacing */}
        <Paper elevation={0} sx={{
          p: 3,  // Increase padding from 2 to 3
          py: 3.5, // Add more vertical padding
          mt: 2,   // Add top margin
          mb: 4,   // Increase bottom margin from 3 to 4
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Author info removed */}

            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ ml: 0 }} // Remove left margin since we removed the author box
            >
              <Link
                underline="hover"
                color="inherit"
                onClick={() => navigate('/')}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                Home
              </Link>
              {post.category && (
                <Link
                  underline="hover"
                  color="inherit"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/?category=${post.category}`)}
                >
                  {post.category}
                </Link>
              )}
              <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                {post.title.length > 30 ? post.title.substring(0, 30) + '...' : post.title}
              </Typography>
            </Breadcrumbs>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={handleWebShare}
              sx={{ bgcolor: 'background.paper', width: 36, height: 36 }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleBookmark}
              sx={{ bgcolor: 'background.paper', width: 36, height: 36 }}
            >
              <BookmarkBorderIcon fontSize="small" color={isBookmarked ? "primary" : "inherit"} />
            </IconButton>
            {isAdmin && (
              <Button
                startIcon={<EditIcon />}
                variant="outlined"
                size="small"
                onClick={() => navigate(`/admin/posts/${id}`)}
                sx={{ ml: 1 }} // Add left margin
              >
                Edit
              </Button>
            )}
          </Box>
        </Paper>

        {!post.thumbnailData && (
          <Box sx={{ mb: 4 }}>
            {/* Remove category chip here too */}
            <Typography variant="h3" component="h1" sx={{
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
            }}>
              {post.title}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36, mr: 1 }}>
                  {post.author.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {post.author}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DateRangeIcon sx={{ color: 'text.secondary', mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(post.created_at)}
                </Typography>
              </Box>
            </Box>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                {post.tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Content */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                mb: 4,
                typography: 'body1',
                color: theme.palette.text.primary,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                '& h1, & h2, & h3, & h4': {
                  color: theme.palette.primary.main,
                  fontWeight: 'bold'
                },
                '& h1': {
                  fontSize: '2rem',
                  fontWeight: 700,
                  mt: 4,
                  mb: 2,
                  lineHeight: 1.3,
                  color: theme.palette.primary.dark
                },
                '& h2': {
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  mt: 3.5,
                  mb: 2,
                  lineHeight: 1.3
                },
                '& h3': {
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  mt: 3,
                  mb: 1.5,
                  lineHeight: 1.3
                },
                '& h4': {
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  mt: 3,
                  mb: 1.5,
                  lineHeight: 1.3
                },
                '& p': {
                  mb: 2,
                  fontSize: '1.05rem',
                  lineHeight: 1.7
                },
                '& blockquote': {
                  borderLeft: `4px solid ${theme.palette.secondary.main}`,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  pl: 2,
                  py: 1,
                  my: 3,
                  mx: 0,
                  color: theme.palette.text.primary,
                  fontStyle: 'italic',
                  borderRadius: '0 4px 4px 0'
                },
                '& ul, & ol': {
                  pl: 4,
                  mb: 2
                },
                '& li': {
                  mb: 1,
                  pl: 1
                },
                '& img, & .post-image img': {
                  maxWidth: '100%',
                  borderRadius: 2,
                  my: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                },
                '& strong': {
                  fontWeight: 600,
                  color: theme.palette.primary.dark
                },
                '& em': {
                  fontStyle: 'italic',
                  color: theme.palette.text.secondary
                }
              }}
            >
              <Box dangerouslySetInnerHTML={renderContent(post.content)} />

              <Divider sx={{ my: 4 }} />

              {/* Share Buttons */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Share this post
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <IconButton
                    onClick={() => handleSocialShare('facebook')}
                    sx={{
                      bgcolor: '#1877F2',
                      color: 'white',
                      '&:hover': { bgcolor: '#0e66d5' }
                    }}>
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleSocialShare('twitter')}
                    sx={{
                      bgcolor: '#1DA1F2',
                      color: 'white',
                      '&:hover': { bgcolor: '#0d8ad3' }
                    }}>
                    <TwitterIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleSocialShare('linkedin')}
                    sx={{
                      bgcolor: '#0A66C2',
                      color: 'white',
                      '&:hover': { bgcolor: '#0952a0' }
                    }}>
                    <LinkedInIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Tags at bottom */}
              {post.tags && post.tags.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {post.tags.map(tag => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="medium"
                        onClick={() => navigate(`/?tag=${tag}`)}
                        sx={{
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: 80 }}>


              {/* Related Tags */}
              {post.tags && post.tags.length > 0 && (
                <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <Box sx={{
                    p: 2,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    borderBottom: `4px solid ${theme.palette.secondary.main}`
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Related Tags
                    </Typography>
                  </Box>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {post.tags.map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          onClick={() => navigate(`/?tag=${tag}`)}
                          sx={{
                            mb: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            color: theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Search Box */}
              <Card sx={{ mt: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Box sx={{
                  p: 2,
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  borderBottom: `4px solid ${theme.palette.secondary.main}`
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Search Articles
                  </Typography>
                </Box>
                <CardContent sx={{ py: 3 }}>
                  <Box component="form" onSubmit={handleSearch}>
                    <TextField
                      fullWidth
                      placeholder="Search for scholarships, universities, programs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                          },
                        }
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
                      }}
                    >
                      Search
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <Card sx={{ mt: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <Box sx={{
                    p: 2,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    borderBottom: `4px solid ${theme.palette.secondary.main}`
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Related Posts
                    </Typography>
                  </Box>
                  <CardContent sx={{ pt: 2, pb: 1 }}>
                    {relatedPosts.map((relatedPost) => (
                      <CardActionArea
                        key={relatedPost._id || relatedPost.id}
                        onClick={() => navigate(`/blog/${relatedPost._id || relatedPost.id}`)}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          overflow: 'hidden',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                          {relatedPost.thumbnailData ? (
                            <Box
                              sx={{
                                width: 70,
                                height: 70,
                                borderRadius: 1,
                                backgroundImage: `url(${relatedPost.thumbnailData.data})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                mr: 2,
                                flexShrink: 0
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 70,
                                height: 70,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2,
                                flexShrink: 0
                              }}
                            >
                              <ArticleIcon sx={{ color: theme.palette.primary.main }} />
                            </Box>
                          )}
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {relatedPost.title.length > 60
                                ? `${relatedPost.title.substring(0, 60)}...`
                                : relatedPost.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(relatedPost.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      </CardActionArea>
                    ))}

                    <Button
                      fullWidth
                      sx={{
                        mt: 1,
                        color: theme.palette.primary.main,
                        fontWeight: 'medium',
                      }}
                      onClick={() => navigate(`/?category=${post.category}`)}
                    >
                      View All {post.category} Posts
                    </Button>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PostDetail;